import { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Link, Menu, MenuItem, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { formatCrmDateTime, localDayKey } from '../../CRM/kanban-board/formatters/crm-datetime';
import { getCrmColors } from '../../CRM/kanban-board/crm-theme';
import { sourceLabel } from '@layouts/pages/CRM/kanban-board/formatters/lead-source-label';
import { ELeadSource, LeadStageResponse } from '../../CRM/kanban-board/services/responses/lead.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { useLeadStatus } from '@layouts/pages/CRM/kanban-board/hooks/use-lead-status.hook';
import { CrmLeadService } from '@layouts/pages/CRM/kanban-board/services/crm-lead.service';
import { MultichannelService } from '../services/multichannel.service';
import { AiAssistant } from '@components/AiAssistant';
import {
    MultichannelConversation,
    MultichannelLead,
} from '../services/responses/multichannel.response';

/// Espelha o limite do backend (GetMultichannelLeadQueryHandler.PanelEvents).
const PANEL_HISTORY_LIMIT = 5;

interface Props {
    conversation: MultichannelConversation | null;
    /// Avisa o pai qual lead está em tela, para a conversa preencher as variáveis da resposta rápida.
    onLoaded?: (lead: MultichannelLead | null) => void;
    /// Recarrega com a conversa: mover o lead de coluna com a tela aberta precisa aparecer aqui.
    signal: number;
}


export const MultichannelLeadPanel = ({ conversation, signal, onLoaded }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const [lead, setLead] = useState<MultichannelLead | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
    const [stageMenu, setStageMenu] = useState<{
        anchor: HTMLElement;
        stages: LeadStageResponse[];
        currentId: string;
    } | null>(null);

    const { showAlert } = useAlert();
    const { statuses } = useLeadStatus();

    /// Sobe quando algo muda por aqui, para o painel recarregar sem depender do sinal externo.
    const [reloadKey, setReloadKey] = useState(0);

    const conversationId = conversation?.id;
    const inboxId = conversation?.inboxId;

    useEffect(() => {
        if (!conversationId || inboxId === null || inboxId === undefined) {
            setLead(null);
            onLoaded?.(null);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setFailed(false);

            try {
                const reply = await new MultichannelService().lead(conversationId, inboxId);

                if (!cancelled) {
                    setLead(reply.data.data ?? null);
                    onLoaded?.(reply.data.data ?? null);
                }
            } catch {
                // O painel é acessório: falhar aqui não pode derrubar a conversa.
                if (!cancelled) {
                    setLead(null);
            onLoaded?.(null);
                    setFailed(true);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();

        return () => { cancelled = true; };
    }, [conversationId, inboxId, signal, reloadKey]);

    const changeStatus = async (statusId: string) => {
        if (!lead) return;

        setStatusAnchor(null);

        try {
            await new CrmLeadService().changeStatus(lead.leadId, statusId);

            showAlert(t('general.atualizacao'), 'success', 2000);
            setReloadKey((v) => v + 1);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    /// As etapas vêm do detalhe do lead só ao abrir o menu: o painel não carrega o quadro.
    const openStages = async (anchor: HTMLElement) => {
        if (!lead) return;

        try {
            const reply = await new CrmLeadService().detail(lead.leadId);
            const { stages, lead: detail } = reply.data.data;

            setStageMenu({ anchor, stages, currentId: detail.columnId });
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    /// Mesmo movimento da tela do lead: entra no topo da coluna de destino.
    const changeStage = async (columnId: string) => {
        if (!lead || !stageMenu) return;

        const currentId = stageMenu.currentId;
        setStageMenu(null);

        if (columnId === currentId) return;

        try {
            await new CrmLeadService().move(lead.leadId, { targetColumnId: columnId, targetSortOrder: 0 });

            showAlert(t('general.atualizacao'), 'success', 2000);
            setReloadKey((v) => v + 1);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    /// Leva à agenda no dia do compromisso, em vez de a semana cheia obrigar a procurá-lo.
    const goToAgenda = (when: string) =>
        navigate(`/crm/agenda?dia=${localDayKey(when)}`);

    const Label = ({ children }: { children: React.ReactNode }) => (
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                color: cc.textMuted,
                fontSize: '10.5px',
                fontWeight: 700,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                mb: 0.6,
            }}
        >
            {children}
        </Typography>
    );

    const Value = ({ children }: { children: React.ReactNode }) => (
        <Typography variant="body2" sx={{ color: cc.textPrimary, fontSize: '13px' }}>
            {children}
        </Typography>
    );

    const frame = (content: React.ReactNode) => (
        <Box
            sx={{
                width: 290,
                flexShrink: 0,
                borderLeft: `1px solid ${cc.border}`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflowY: 'auto',
            }}
        >
            {content}
        </Box>
    );

    if (!conversation) return frame(null);

    if (isLoading) {
        return frame(
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Skeleton height={22} width="45%" />
                <Skeleton height={54} variant="rounded" />
                <Skeleton height={54} variant="rounded" />
                <Skeleton height={90} variant="rounded" />
            </Box>
        );
    }

    if (failed) {
        return frame(
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: cc.eventDanger }}>
                    {t('crmMultichannel.erroPainel')}
                </Typography>
            </Box>
        );
    }

    // LeadId vazio é conversa que ainda não virou lead, não erro; o painel diferencia os dois.
    if (!lead || !lead.leadId || lead.leadId === '00000000-0000-0000-0000-000000000000') {
        return frame(
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <PersonOutlineIcon sx={{ fontSize: 34, color: cc.textMuted, mb: 1 }} />
                <Typography variant="body2" sx={{ color: cc.textSecondary, fontSize: '13px' }}>
                    {t('crmMultichannel.conversaSemLead')}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ display: 'block', color: cc.textMuted, mt: 0.8, lineHeight: 1.5 }}
                >
                    {t('crmMultichannel.conversaSemLeadAjuda')}
                </Typography>

                <Box sx={{ mt: 2, textAlign: 'left' }}>
                    <AiAssistant key={conversation.id} conversationId={conversation.id} inboxId={conversation.inboxId} />
                </Box>
            </Box>
        );
    }

    const task = lead.nextSchedule;
    const overdue = task ? new Date(task.scheduledAt) < new Date() : false;

    return frame(
        <>
            <Box
                sx={{
                    p: '14px 16px',
                    borderBottom: `1px solid ${cc.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: cc.textSecondary,
                        fontSize: '12px',
                        fontWeight: 700,
                        letterSpacing: '.05em',
                        textTransform: 'uppercase',
                    }}
                >
                    {t('crmMultichannel.painelLead')}
                </Typography>

                <Link
                    component="button"
                    underline="hover"
                    onClick={() => navigate(`/crm/quadro-kanban?focoLead=${lead.leadId}`)}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: cc.accent,
                        fontSize: '11.5px',
                        fontWeight: 500,
                    }}
                >
                    {t('crmMultichannel.abrirLead')}
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
            </Box>

            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <AiAssistant
                    key={conversation.id}
                    conversationId={conversation.id}
                    inboxId={conversation.inboxId}
                    leadId={lead.leadId}
                    onApplied={() => setReloadKey((v) => v + 1)}
                />

                {/* Etapa e status lado a lado: são dimensões diferentes e não se confundem. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                    <Box>
                        <Label>{t('crmMultichannel.etapa')}</Label>
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={(e) => openStages(e.currentTarget)}
                            onKeyDown={(e) => e.key === 'Enter' && openStages(e.currentTarget)}
                            title={lead.columnName ?? undefined}
                            sx={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'top',
                                bgcolor: cc.accentSoftBg,
                                color: cc.onTint,
                                fontSize: '12px',
                                fontWeight: 700,
                                borderRadius: '6px',
                                p: '5px 9px',
                                cursor: 'pointer',
                                '&:hover': { filter: 'brightness(1.1)' },
                            }}
                        >
                            {lead.columnName ?? '—'}
                        </Box>
                    </Box>

                    <Box>
                        <Label>{t('crmMultichannel.status')}</Label>
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={(e) => setStatusAnchor(e.currentTarget)}
                            onKeyDown={(e) => e.key === 'Enter' && setStatusAnchor(e.currentTarget)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.8,
                                bgcolor: cc.countBg,
                                fontSize: '12px',
                                fontWeight: 700,
                                borderRadius: '6px',
                                p: '5px 9px',
                                cursor: 'pointer',
                                color: lead.statusColor ?? cc.textSecondary,
                                '&:hover': { bgcolor: cc.menuBg },
                            }}
                        >
                            {lead.statusColor && (
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: lead.statusColor,
                                    }}
                                />
                            )}
                            {lead.statusName ?? '—'}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                    <Box>
                        <Label>{t('crmMultichannel.responsavel')}</Label>
                        <Value>
                            {lead.assignedUserName ?? (
                                <Box component="span" sx={{ color: cc.textMuted }}>
                                    {t('crmMultichannel.semResponsavel')}
                                </Box>
                            )}
                        </Value>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Label>{t('crmMultichannel.origem')}</Label>
                        {/* Campanha na linha da origem: em linha própria, a célula abria um vão sob o responsável. */}
                        <Tooltip title={lead.sourceCampaign ?? ''}>
                            <Typography variant="body2" noWrap sx={{ color: cc.textPrimary, fontSize: '13px' }}>
                                {sourceLabel(t, lead.source as ELeadSource, lead.sourceDetail)}
                                {lead.sourceCampaign && (
                                    <Box component="span" sx={{ color: cc.textMuted }}>
                                        {` · ${lead.sourceCampaign}`}
                                    </Box>
                                )}
                            </Typography>
                        </Tooltip>
                    </Box>

                    <Box>
                        <Label>{t('crmMultichannel.criadoEm')}</Label>
                        <Value>{formatCrmDateTime(lead.createdAt)}</Value>
                    </Box>
                </Box>

                <Box sx={{ height: '1px', bgcolor: cc.border }} />

                <Box>
                    <Label>{t('crmMultichannel.proximaTarefa')}</Label>

                    {task ? (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => goToAgenda(task.scheduledAt)}
                            onKeyDown={(e) => e.key === 'Enter' && goToAgenda(task.scheduledAt)}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.2,
                                bgcolor: cc.countBg,
                                border: `1px solid ${cc.border}`,
                                borderRadius: '9px',
                                p: '10px 11px',
                                cursor: 'pointer',
                                '&:hover': { borderColor: cc.borderHover },
                            }}
                        >
                            <EventOutlinedIcon
                                sx={{
                                    fontSize: 16,
                                    flexShrink: 0,
                                    mt: '1px',
                                    color: overdue ? cc.scheduleLate : cc.scheduleSoon,
                                }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        color: cc.textPrimary,
                                        fontSize: '12.5px',
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {task.description}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        mt: 0.4,
                                        fontSize: '11px',
                                        fontWeight: overdue ? 700 : 400,
                                        color: overdue ? cc.scheduleLate : cc.textSecondary,
                                    }}
                                >
                                    {formatCrmDateTime(task.scheduledAt)}
                                    {overdue ? ` · ${t('crmMultichannel.tarefaAtrasada')}` : ''}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="caption" sx={{ color: cc.textMuted }}>
                            {t('crmMultichannel.semProximaTarefa')}
                        </Typography>
                    )}
                </Box>

                <Box>
                    <Label>{t('crmMultichannel.historico')}</Label>

                    {lead.history.length > 0 && (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: cc.textMuted, fontSize: '11px', mt: -0.4, mb: 1 }}
                        >
                            {t('crmMultichannel.ultimasAlteracoes', { total: PANEL_HISTORY_LIMIT })}
                        </Typography>
                    )}

                    {lead.history.length === 0 ? (
                        <Typography variant="caption" sx={{ color: cc.textMuted }}>
                            {t('crmMultichannel.semHistorico')}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                            {lead.history.map((event, index) => (
                                <Box key={event.id} sx={{ display: 'flex', gap: 1.2 }}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            mt: '6px',
                                            bgcolor: index === 0 ? cc.accent : cc.borderHover,
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                color: cc.textPrimary,
                                                fontSize: '12.5px',
                                                lineHeight: 1.4,
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {event.description}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                mt: 0.3,
                                                fontSize: '10.5px',
                                                color: cc.textMuted,
                                            }}
                                        >
                                            {formatCrmDateTime(event.createdAt)}
                                            {event.userName ? ` · ${event.userName}` : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Mesmo menu do card do quadro: trocar a temperatura é a mesma ação. */}
            <Menu
                anchorEl={statusAnchor}
                open={Boolean(statusAnchor)}
                onClose={() => setStatusAnchor(null)}
                PaperProps={{
                    sx: {
                        bgcolor: cc.menuBg,
                        backgroundImage: lead.statusColor
                            ? `linear-gradient(${alpha(lead.statusColor, 0.18)}, ${alpha(lead.statusColor, 0.18)})`
                            : undefined,
                        border: `1px solid ${lead.statusColor ? alpha(lead.statusColor, 0.55) : cc.border}`,
                    },
                }}
            >
                {statuses.map((status) => (
                    <MenuItem
                        key={status.id}
                        // Sem statusId no payload, o atual é reconhecido pelo nome, que o painel tem em mãos.
                        selected={status.name === lead.statusName}
                        onClick={() => changeStatus(status.id)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, color: cc.textPrimary }}
                    >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status.color }} />
                        <Typography variant="caption" fontWeight={700}>{status.name}</Typography>
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={stageMenu?.anchor}
                open={Boolean(stageMenu)}
                onClose={() => setStageMenu(null)}
                // Largura travada: nome de etapa longo termina em reticências em vez de atravessar a tela.
                PaperProps={{ sx: { bgcolor: cc.menuBg, border: `1px solid ${cc.border}`, maxWidth: 240 } }}
            >
                {stageMenu?.stages.map((stage) => (
                    <MenuItem
                        key={stage.id}
                        selected={stage.id === stageMenu.currentId}
                        onClick={() => changeStage(stage.id)}
                        sx={{ color: cc.textPrimary }}
                    >
                        <Typography variant="caption" fontWeight={700} noWrap title={stage.name}>{stage.name}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
